import type { BatchEntryMethod, ResolvedSerialLine } from '@/lib/types';

export type OutcomeTone = 'success' | 'warning' | 'error' | 'muted';

export interface BatchLineOutcome {
  status: string;
  label: string;
  message: string;
  tone: OutcomeTone;
}

export interface BatchLine {
  /** Client-side id, stable for the life of the screen. */
  id: number;
  serial: string;
  entryMethod: BatchEntryMethod;
  /** null while the register lookup is in flight. */
  resolved: ResolvedSerialLine | null;
  /** Set once the line has been sent to the server (deploy / inspect). */
  outcome?: BatchLineOutcome;
}

const STATUS_TONE: Record<ResolvedSerialLine['status'], OutcomeTone> = {
  ready: 'success',
  blocked: 'error',
  not_found: 'error',
  duplicate: 'warning',
};

export const TONE_TEXT: Record<OutcomeTone, string> = {
  success: 'text-green-700',
  warning: 'text-amber-700',
  error: 'text-red-700',
  muted: 'text-gray-500',
};

export const TONE_BADGE: Record<OutcomeTone, string> = {
  success: 'bg-green-100 text-green-800 border-green-200',
  warning: 'bg-amber-100 text-amber-800 border-amber-200',
  error: 'bg-red-100 text-red-800 border-red-200',
  muted: 'bg-gray-100 text-gray-700 border-gray-200',
};

export function lineCategory(line: BatchLine): { key: string; label: string } | null {
  const asset = line.resolved?.asset;
  if (!asset) return null;
  return { key: asset.type, label: asset.type_display };
}

export function lineStatusLabel(line: BatchLine): string {
  if (line.outcome) return line.outcome.label;
  if (!line.resolved) return 'Looking up…';
  switch (line.resolved.status) {
    case 'ready':
      return 'Ready';
    case 'blocked':
      return 'Blocked';
    case 'not_found':
      return 'Not found';
    case 'duplicate':
      return 'Duplicate';
    default:
      return line.resolved.status;
  }
}

export function lineTone(line: BatchLine): OutcomeTone {
  if (line.outcome) return line.outcome.tone;
  if (!line.resolved) return 'muted';
  return STATUS_TONE[line.resolved.status];
}
